// ===========================================
// Admin Meet & Greet Management Page
// ===========================================
// List and manage meet & greet bookings

import { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Skeleton,
  Tooltip,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import ListIcon from '@mui/icons-material/List';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VerifiedIcon from '@mui/icons-material/Verified';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditIcon from '@mui/icons-material/Edit';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
// ONE "send this again" glyph for BOTH resend actions (envelope + outgoing
// arrow). Deliberately not MarkEmailReadIcon, which already means "Email Sent"
// as a STATUS elsewhere in the admin (InvoicesPage), nor SendIcon, which is the
// first-send idiom on Parents/Students.
//
// The two resends are therefore separated by COLOUR (verification #4580E4 /
// registration #FFCE00, each matching its own email's CTA) plus distinct
// tooltips, aria-labels and a confirm modal that names the email and its
// recipients — so colour is never the only thing carrying the meaning.
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import AddIcon from '@mui/icons-material/Add';
import {
  useMeetAndGreets,
  useMeetAndGreet,
  useMeetAndGreetCounts,
  useApproveMeetAndGreet,
  useRejectMeetAndGreet,
  useCancelMeetAndGreet,
  useUpdateMeetAndGreet,
  useMarkMeetAndGreetVerified,
  useWaitlistMeetAndGreet,
  useResendRegistrationLink,
  useResendVerificationLink,
} from '../../hooks/useMeetAndGreet';
import { MeetAndGreet, MeetAndGreetStatus } from '../../api/meetAndGreet.api';
import { format } from 'date-fns';
import MeetAndGreetSettings from '../../components/admin/MeetAndGreetSettings';
import MeetAndGreetAvailabilitySettings from '../../components/admin/MeetAndGreetAvailabilitySettings';
import AdminScheduleDialog from '../../components/admin/AdminScheduleDialog';
import EditMeetAndGreetDetailsDialog from '../../components/admin/EditMeetAndGreetDetailsDialog';
import ConvertToStudentDialog from '../../components/admin/ConvertToStudentDialog';
import CreateMeetAndGreetDialog from '../../components/admin/CreateMeetAndGreetDialog';
import { usePermissions } from '../../hooks/usePermissions';
import { useTenant } from '../../contexts/TenantContext';
import { useTeachers } from '../../hooks/useUsers';
import { useInstruments } from '../../hooks/useAdmin';
import type { Teacher } from '../../api/users.api';
import SpamCheckChip, { MEET_AND_GREET_SPAM_CHECK_TOOLTIP } from '../../components/admin/SpamCheckChip';
import LinkedEnquiryChip from '../../components/admin/enquiries/LinkedEnquiryChip';
import MeetAndGreetNotesSection from '../../components/meetAndGreet/MeetAndGreetNotesSection';
import { NotesMissingChip } from '../../components/meetAndGreet/MeetAndGreetNotesList';
import { WaitlistClockInfo, WaitlistReviewActions } from '../../components/meetAndGreet/WaitlistReview';

// ===========================================
// STATUS CONFIGURATION
// ===========================================

// Brand colors for status chips
const statusConfig: Record<
  MeetAndGreetStatus,
  { label: string; bgcolor: string; color: string }
> = {
  PENDING_VERIFICATION: { label: 'Pending Verification', bgcolor: '#9DA5AF', color: 'white' }, // Grey
  PENDING_APPROVAL: { label: 'Pending Approval', bgcolor: '#FFCE00', color: '#080808' }, // Yellow (secondary)
  APPROVED: { label: 'Approved', bgcolor: '#4580E4', color: 'white' }, // Blue (primary)
  WAITLISTED: { label: 'Waitlisted', bgcolor: '#FFAE9E', color: '#080808' }, // Coral (accent)
  REJECTED: { label: 'Rejected', bgcolor: '#ff4040', color: 'white' }, // Red (error)
  CONVERTED: { label: 'Converted', bgcolor: '#96DAC9', color: '#080808' }, // Mint (accent)
  CANCELLED: { label: 'Cancelled', bgcolor: '#9DA5AF', color: 'white' }, // Grey
};

// ===========================================
// COMPONENT
// ===========================================

/**
 * "Send" for a booking whose verification email never went out (an admin-created
 * Initial-form booking with the email switch off, #425 /qa L4), "Resend" otherwise.
 * A very old public booking without the stamp also reads "Send"; harmless (it sends).
 */
function verificationEmailSent(booking: MeetAndGreet | null): boolean {
  return Boolean(booking?.verificationTokenSentAt || booking?.verificationTokenResentAt);
}

/**
 * #412 (/qa Phase 4 L6): `spamCheck` stays UNVERIFIED for the booking's life, so the
 * warning only asks for action while the family is still waiting and nothing was sent.
 */
function spamCheckActionable(booking: MeetAndGreet): boolean {
  return booking.status === 'PENDING_VERIFICATION' && !verificationEmailSent(booking);
}

/**
 * #430: why Approve and Resend are off for a booking whose contact is already a
 * family (or has a login). One text, used by the tooltip and the aria-label.
 */
const EXISTING_FAMILY_TIP =
  'This family already has an account. Add the child on the Students page (Add Student, then choose their family).';

/** #430: true when the server says this booking's contact belongs to an existing family or login. */
function hasExistingFamily(booking: MeetAndGreet | null | undefined): boolean {
  return Boolean(booking?.existingFamily);
}

/** Statuses the backend lets an admin Convert (adminConvertMeetAndGreet). #425 /qa H1. */
const CONVERTIBLE_STATUSES: MeetAndGreetStatus[] = ['APPROVED', 'PENDING_APPROVAL', 'WAITLISTED'];

/**
 * The Resend registration tooltip (#249). Resend mails BOTH contacts, so no single
 * address is named.
 */
function resendRegistrationLabel(booking: MeetAndGreet): string {
  if (!booking.canResendRegistration) {
    // #214: a converted booking can be set back to APPROVED; it already has a family.
    return booking.convertedFamilyId
      ? 'This booking was already converted to a family — no registration link can be sent'
      : 'No registration link was ever sent for this booking — use "Convert to Student" instead';
  }
  return booking.hasRegistrationToken
    ? 'Resend the registration & payment link to both contacts'
    : "The earlier link was cancelled. Resend sends a new one to the family's current email address(es)";
}

/**
 * #430: the "Existing family" tag (mint). "Has a login" when the match is a login with no
 * active family. "Open family" only for an office user who may read families, and only when
 * the server named the family's PRIMARY parent (the families page is keyed on that id).
 */
function ExistingFamilyChip({ booking }: { booking: MeetAndGreet }) {
  const { can } = usePermissions();
  const { schoolSlug } = useTenant();
  const match = booking.existingFamily;
  if (!match) return null;
  const canOpen = can('people_families', 'read') && Boolean(match.primaryParentId);
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      <Tooltip title={EXISTING_FAMILY_TIP}>
        <Chip
          label={match.loginWithoutFamily ? 'Has a login' : 'Existing family'}
          size="small"
          sx={{ bgcolor: '#96DAC9', color: '#080808' }}
        />
      </Tooltip>
      {canOpen && (
        <Typography
          component={RouterLink}
          to={`/${schoolSlug}/admin/families?parent=${encodeURIComponent(match.primaryParentId as string)}`}
          variant="caption"
          sx={{ color: 'primary.main' }}
        >
          Open family
        </Typography>
      )}
    </Box>
  );
}

export default function MeetAndGreetPage() {
  const [mainTab, setMainTab] = useState<'bookings' | 'settings'>('bookings');
  // Same dialog as the Calendar's "New booking → Meet & Greet". Gated on the
  // permission its route requires (POST /admin/meet-and-greet/admin/create).
  const [createOpen, setCreateOpen] = useState(false);
  const { can, isOwner } = usePermissions();
  // #292: write controls follow the route each one calls (see md/review/292-plan.md H1).
  // Cancel is DELETE, so it needs meet_and_greet.delete, not write.
  const canWrite = can('meet_and_greet', 'write');
  const canCancel = can('meet_and_greet', 'delete');
  const canCreate = canWrite;
  const [statusFilter, setStatusFilter] = useState<MeetAndGreetStatus | 'ALL'>('ALL');
  const [selectedBooking, setSelectedBooking] = useState<MeetAndGreet | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [waitlistDialogOpen, setWaitlistDialogOpen] = useState(false);
  const [waitlistReason, setWaitlistReason] = useState('');
  const [waitlistNotifyParent, setWaitlistNotifyParent] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<
    'verify' | 'approve' | 'cancel' | 'resend-registration' | 'resend-verification' | null
  >(null);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  /**
   * Holds a registration link that was regenerated but could NOT be emailed.
   * The previous link is already dead at this point, so the admin needs the URL
   * in a surface they can copy from — a snackbar disappears before they can.
   */
  const [manualLink, setManualLink] = useState<{
    url: string;
    message: string;
    failures: string[];
  } | null>(null);
  const [manualLinkCopied, setManualLinkCopied] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusMenuBooking, setStatusMenuBooking] = useState<MeetAndGreet | null>(null);
  const [teacherMenuAnchor, setTeacherMenuAnchor] = useState<null | HTMLElement>(null);
  const [teacherMenuBooking, setTeacherMenuBooking] = useState<MeetAndGreet | null>(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const [instrumentFilter, setInstrumentFilter] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('');

  // Queries
  const {
    data: bookings,
    isLoading,
    isFetching: listFetching,
    isPlaceholderData: listPlaceholder,
    refetch,
    error: bookingsError,
    isError: bookingsIsError,
  } = useMeetAndGreets(
    statusFilter === 'ALL' ? undefined : { status: statusFilter }
  );
  const { data: counts, error: countsError, isError: countsIsError } = useMeetAndGreetCounts();

  // Mutations
  const approveMutation = useApproveMeetAndGreet();
  const rejectMutation = useRejectMeetAndGreet();
  const cancelMutation = useCancelMeetAndGreet();
  const updateMutation = useUpdateMeetAndGreet();
  const markVerifiedMutation = useMarkMeetAndGreetVerified();
  const waitlistMutation = useWaitlistMeetAndGreet();
  const resendRegistrationMutation = useResendRegistrationLink();
  const resendVerificationMutation = useResendVerificationLink();

  // Teachers for assignment and filtering
  const { data: teachers } = useTeachers();
  const { data: instrumentsData } = useInstruments();
  const instruments = instrumentsData ?? [];

  // `?open=<id>` (the calendar's Meet & Greet event) opens that booking's detail once the list is
  // in, then strips the parameter so a refresh or a back-step does not reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get('open');
  // L1 (whole-feature review): the list may be a stale cache (the refetch is still running) or
  // placeholder data, which cannot prove a booking is absent. Judge it only once SETTLED; if the id
  // is still not in it, ask the server for that one booking, and say so when it is not found.
  const { enqueueSnackbar } = useSnackbar();
  const listSettled = Array.isArray(bookings) && !listFetching && !listPlaceholder;
  const openInList = openId && listSettled ? bookings.find((b) => b.id === openId) : undefined;
  const openFetch = useMeetAndGreet(openId ?? '', !!openId && listSettled && !openInList);
  const openHandled = useRef<string | null>(null);
  const openFetched = openFetch.data;
  const openFetchFailed = openFetch.isError;
  const openFetchDone = !openFetch.isFetching;
  useEffect(() => {
    if (!openId || openHandled.current === openId) return;
    let target: MeetAndGreet | undefined = openInList;
    if (!target) {
      if (!listSettled || !openFetchDone) return;
      if (openFetched) target = openFetched;
      else if (!openFetchFailed) return; // the by-id read has not started yet
    }
    openHandled.current = openId;
    if (target) {
      setSelectedBooking(target);
      setDetailDialogOpen(true);
    } else {
      enqueueSnackbar('That Meet & Greet was not found.', { variant: 'warning' });
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('open');
        return next;
      },
      { replace: true },
    );
  }, [openId, openInList, listSettled, openFetched, openFetchFailed, openFetchDone, enqueueSnackbar, setSearchParams]);

  const liveRow = selectedBooking && Array.isArray(bookings) ? bookings.find((b) => b.id === selectedBooking.id) : undefined;
  const detailEnquiry = liveRow ? liveRow.enquiry : selectedBooking?.enquiry;

  // Handlers
  const handleViewDetails = (booking: MeetAndGreet) => {
    setSelectedBooking(booking);
    setDetailDialogOpen(true);
  };

  const handleApproveClick = (booking: MeetAndGreet) => {
    setSelectedBooking(booking);
    setConfirmAction('approve');
    setConfirmDialogOpen(true);
  };

  const handleRejectClick = (booking: MeetAndGreet) => {
    setSelectedBooking(booking);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleCancelClick = (booking: MeetAndGreet) => {
    setSelectedBooking(booking);
    setConfirmAction('cancel');
    setConfirmDialogOpen(true);
  };

  const handleConvertClick = (booking: MeetAndGreet) => {
    setSelectedBooking(booking);
    setConvertDialogOpen(true);
  };

  const handleWaitlistClick = (booking: MeetAndGreet) => {
    setSelectedBooking(booking);
    setWaitlistReason('');
    setWaitlistNotifyParent(true);
    setWaitlistDialogOpen(true);
  };

  const handleVerifyClick = (booking: MeetAndGreet) => {
    setSelectedBooking(booking);
    setConfirmAction('verify');
    setConfirmDialogOpen(true);
  };

  const handleResendRegistrationClick = (booking: MeetAndGreet) => {
    setSelectedBooking(booking);
    setConfirmAction('resend-registration');
    setConfirmDialogOpen(true);
  };

  const handleResendVerificationClick = (booking: MeetAndGreet) => {
    setSelectedBooking(booking);
    setConfirmAction('resend-verification');
    setConfirmDialogOpen(true);
  };

  /**
   * "Last sent" for the confirm dialog — a resend supersedes the original send.
   *
   * These are true instants (timestamp), not @db.Date calendar days, so local
   * getters via format() are correct here; the UTC-getter rule does not apply.
   * Rows issued before the token-hardening migration have no timestamp at all —
   * "Not recorded" is deliberate, because the only column that could have been
   * backfilled (updatedAt) is frozen at row creation on this model and would
   * have shown a misleading age.
   */
  const lastSentLabel = (
    booking: MeetAndGreet,
    kind: 'registration' | 'verification'
  ): string => {
    const raw =
      kind === 'registration'
        ? booking.registrationTokenResentAt ?? booking.registrationTokenSentAt
        : booking.verificationTokenResentAt ?? booking.verificationTokenSentAt;

    if (!raw) return 'Not recorded';

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return 'Not recorded';

    return format(parsed, 'd MMM yyyy, h:mm a');
  };

  const handleStatusMenuOpen = (event: React.MouseEvent<HTMLElement>, booking: MeetAndGreet) => {
    setStatusMenuAnchor(event.currentTarget);
    setStatusMenuBooking(booking);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
    setStatusMenuBooking(null);
  };

  const handleTeacherMenuOpen = (event: React.MouseEvent<HTMLElement>, booking: MeetAndGreet) => {
    setTeacherMenuAnchor(event.currentTarget);
    setTeacherMenuBooking(booking);
  };

  const handleTeacherMenuClose = () => {
    setTeacherMenuAnchor(null);
    setTeacherMenuBooking(null);
  };

  const handleTeacherAssign = async (teacherId: string | null) => {
    if (!teacherMenuBooking) return;
    handleTeacherMenuClose();
    try {
      const updated = await updateMutation.mutateAsync({
        id: teacherMenuBooking.id,
        data: { assignedTeacherId: teacherId },
      });
      // Update selectedBooking with the response from the mutation. The response does not carry the
      // linked enquiry (only the list and detail do), so keep the one already shown.
      if (selectedBooking?.id === teacherMenuBooking.id) {
        setSelectedBooking({ ...updated, enquiry: selectedBooking.enquiry });
      }
    } catch {
      // Refused (e.g. MNG_CONVERTED): the hook's onError already told the office and refetched.
    }
  };

  const handleEditScheduled = () => {
    if (!selectedBooking) return;
    setScheduleDialogOpen(true);
  };

  const handleSaveScheduled = async (isoDateTime: string) => {
    if (!selectedBooking) return;
    try {
      await updateMutation.mutateAsync({
        id: selectedBooking.id,
        data: { scheduledDateTime: isoDateTime },
      });
    } catch {
      // Refused (e.g. MNG_CONVERTED): the hook's onError already told the office and refetched.
      return;
    }
    // Update selectedBooking to reflect changes in modal
    setSelectedBooking({
      ...selectedBooking,
      scheduledDateTime: isoDateTime,
    });
  };

  const getTeacherColor = (teacherId: string | null) => {
    if (!teacherId || !teachers) return null;
    const teacher = (teachers as Teacher[]).find((t) => t.id === teacherId);
    return teacher?.color || '#4580E4';
  };

  const handleStatusAction = (action: 'verify' | 'approve' | 'reject' | 'waitlist' | 'convert' | 'cancel') => {
    if (!statusMenuBooking) return;
    handleStatusMenuClose();

    if (action === 'verify') {
      handleVerifyClick(statusMenuBooking);
    } else if (action === 'approve') {
      handleApproveClick(statusMenuBooking);
    } else if (action === 'reject') {
      handleRejectClick(statusMenuBooking);
    } else if (action === 'waitlist') {
      handleWaitlistClick(statusMenuBooking);
    } else if (action === 'convert') {
      handleConvertClick(statusMenuBooking);
    } else if (action === 'cancel') {
      handleCancelClick(statusMenuBooking);
    }
  };

  // Get available status transitions based on current status
  const getStatusActions = (booking: MeetAndGreet) =>
    getAllStatusActions(booking).filter((item) =>
      item.action === 'cancel' ? canCancel : canWrite,
    );

  const getAllStatusActions = (booking: MeetAndGreet) => {
    // #430: Approve is off for an existing family (it would issue a registration link);
    // Convert only for a contact-1 login (hardCollision), anything else is overridable in its dialog.
    const familyReason = hasExistingFamily(booking) ? EXISTING_FAMILY_TIP : undefined;
    const convertReason = booking.existingFamily?.hardCollision ? EXISTING_FAMILY_TIP : undefined;
    switch (booking.status) {
      case 'PENDING_VERIFICATION':
        return [
          { action: 'verify' as const, label: 'Mark as Verified', icon: <VerifiedIcon fontSize="small" />, color: '#FFCE00' },
          { action: 'cancel' as const, label: 'Cancel', icon: <DeleteIcon fontSize="small" />, color: '#ff4040' },
        ];
      case 'PENDING_APPROVAL':
        return [
          { action: 'approve' as const, label: 'Approve', icon: <CheckCircleIcon fontSize="small" />, color: '#4caf50', disabledReason: familyReason },
          { action: 'waitlist' as const, label: 'Add to Waitlist', icon: <HourglassEmptyIcon fontSize="small" />, color: '#FFAE9E' },
          { action: 'reject' as const, label: 'Reject', icon: <CancelIcon fontSize="small" />, color: '#ff4040' },
          { action: 'convert' as const, label: 'Convert to Student', icon: <PersonAddIcon fontSize="small" />, color: '#96DAC9', disabledReason: convertReason },
          { action: 'cancel' as const, label: 'Cancel', icon: <DeleteIcon fontSize="small" />, color: '#9DA5AF' },
        ];
      case 'WAITLISTED':
        return [
          { action: 'approve' as const, label: 'Approve', icon: <CheckCircleIcon fontSize="small" />, color: '#4caf50', disabledReason: familyReason },
          { action: 'convert' as const, label: 'Convert to Student', icon: <PersonAddIcon fontSize="small" />, color: '#96DAC9', disabledReason: convertReason },
          { action: 'cancel' as const, label: 'Cancel', icon: <DeleteIcon fontSize="small" />, color: '#9DA5AF' },
        ];
      case 'APPROVED':
        return [
          { action: 'convert' as const, label: 'Convert to Student', icon: <PersonAddIcon fontSize="small" />, color: '#96DAC9', disabledReason: convertReason },
          { action: 'cancel' as const, label: 'Cancel', icon: <DeleteIcon fontSize="small" />, color: '#9DA5AF' },
        ];
      default:
        return [];
    }
  };

  // #430: Approve issues a registration link, which an existing family must never get.
  const renderApproveIcon = (booking: MeetAndGreet) => {
    const blocked = hasExistingFamily(booking);
    return (
      <Tooltip title={blocked ? EXISTING_FAMILY_TIP : 'Approve'}>
        {/* span: MUI needs a non-disabled wrapper to fire the tooltip on a disabled control. */}
        <span>
          <IconButton
            size="small"
            color="success"
            disabled={blocked}
            onClick={() => handleApproveClick(booking)}
            aria-label={blocked ? `Approve (unavailable: ${EXISTING_FAMILY_TIP})` : 'Approve'}
          >
            <CheckCircleIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const handleConfirmAction = async () => {
    if (!selectedBooking) return;

    // A rejected mutation must still close the dialog. The hooks already toast
    // the server's message, so leaving it open strands the admin on a dead
    // dialog whose spinner has cleared. The resend paths have four backend 400
    // guards, so this fires far more often than it did for approve/cancel.
    try {
      if (confirmAction === 'verify') {
        // Its own route since #304: the PATCH answers 400 to a status.
        await markVerifiedMutation.mutateAsync(selectedBooking.id);
      } else if (confirmAction === 'approve') {
        await approveMutation.mutateAsync(selectedBooking.id);
      } else if (confirmAction === 'cancel') {
        await cancelMutation.mutateAsync(selectedBooking.id);
      } else if (confirmAction === 'resend-registration') {
        const result = await resendRegistrationMutation.mutateAsync(selectedBooking.id);
        // Regenerated but undelivered: the parent's previous link is now dead, so
        // give the admin the new URL in a copyable, persistent surface.
        if ((result?.recipients?.length ?? 0) === 0 && result?.registrationUrl) {
          setManualLink({
            url: result.registrationUrl,
            message: result.message,
            failures: result.emailFailures ?? [],
          });
          setManualLinkCopied(false);
        }
      } else if (confirmAction === 'resend-verification') {
        await resendVerificationMutation.mutateAsync(selectedBooking.id);
      }
    } catch {
      // Swallowed deliberately — the mutation's onError already surfaced it.
    } finally {
      setConfirmDialogOpen(false);
      setSelectedBooking(null);
      setConfirmAction(null);
    }
  };

  const handleRejectConfirm = async () => {
    if (!selectedBooking || !rejectReason.trim()) return;

    await rejectMutation.mutateAsync({
      id: selectedBooking.id,
      reason: rejectReason,
    });

    setRejectDialogOpen(false);
    setSelectedBooking(null);
    setRejectReason('');
  };

  const handleWaitlistConfirm = async () => {
    if (!selectedBooking) return;

    await waitlistMutation.mutateAsync({
      id: selectedBooking.id,
      reason: waitlistReason.trim() || undefined,
      notifyParent: waitlistNotifyParent,
    });

    setWaitlistDialogOpen(false);
    setSelectedBooking(null);
    setWaitlistReason('');
    setWaitlistNotifyParent(true);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return format(new Date(dateStr), 'dd MMM yyyy, h:mm a');
  };

  // Client-side filtering by instrument and teacher
  const filteredBookings = (bookings ?? []).filter((b) => {
    if (instrumentFilter && b.instrument?.id !== instrumentFilter) return false;
    if (teacherFilter && b.assignedTeacherId !== teacherFilter) return false;
    return true;
  });

  // Tab counts
  const tabCounts = counts || {
    PENDING_VERIFICATION: 0,
    PENDING_APPROVAL: 0,
    APPROVED: 0,
    WAITLISTED: 0,
    REJECTED: 0,
    CONVERTED: 0,
    CANCELLED: 0,
  };
  const totalCount = Object.values(tabCounts).reduce((a: number, b: number) => a + b, 0);

  return (
    <Box>
      {/* Error Display */}
      {bookingsIsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load bookings: {bookingsError?.message || 'Unknown error'}
        </Alert>
      )}
      {countsIsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load counts: {countsError?.message || 'Unknown error'}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">Meet & Greet</Typography>
        {mainTab === 'bookings' && (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Instrument</InputLabel>
              <Select
                value={instrumentFilter}
                label="Instrument"
                onChange={(e) => setInstrumentFilter(e.target.value)}
              >
                <MenuItem value="">All Instruments</MenuItem>
                {instruments.map((inst) => (
                  <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Teacher</InputLabel>
              <Select
                value={teacherFilter}
                label="Teacher"
                onChange={(e) => setTeacherFilter(e.target.value)}
              >
                <MenuItem value="">All Teachers</MenuItem>
                {(teachers ?? []).map((t: Teacher) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.User.firstName} {t.User.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button startIcon={<RefreshIcon />} onClick={() => { setInstrumentFilter(''); setTeacherFilter(''); refetch(); }}>
              Refresh
            </Button>
            {canCreate && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                Create Meet &amp; Greet
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Main Tabs (Bookings / Settings) */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={mainTab}
          onChange={(_, value) => setMainTab(value)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label="Bookings"
            value="bookings"
            icon={<ListIcon />}
            iconPosition="start"
          />
          <Tab
            label="Settings"
            value="settings"
            icon={<SettingsIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Settings Tab Content */}
      {mainTab === 'settings' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <MeetAndGreetAvailabilitySettings />
          <MeetAndGreetSettings />
        </Box>
      )}

      {/* Bookings Tab Content */}
      {mainTab === 'bookings' && (
        <>
          {/* Status Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={statusFilter}
              onChange={(_, value) => setStatusFilter(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label={`All (${totalCount})`} value="ALL" />
              <Tab
                label={`Pending Verification (${tabCounts.PENDING_VERIFICATION})`}
                value="PENDING_VERIFICATION"
              />
              <Tab
                label={`Pending Approval (${tabCounts.PENDING_APPROVAL})`}
                value="PENDING_APPROVAL"
              />
              <Tab label={`Approved (${tabCounts.APPROVED})`} value="APPROVED" />
              <Tab label={`Waitlisted (${tabCounts.WAITLISTED})`} value="WAITLISTED" />
              <Tab label={`Converted (${tabCounts.CONVERTED})`} value="CONVERTED" />
              <Tab label={`Rejected (${tabCounts.REJECTED})`} value="REJECTED" />
              <Tab label={`Cancelled (${tabCounts.CANCELLED})`} value="CANCELLED" />
            </Tabs>
          </Paper>

          {/* Bookings Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Parent</TableCell>
                  <TableCell>Instrument</TableCell>
                  <TableCell>Assigned Teacher</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date/Time</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  // Loading skeleton
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(7)].map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filteredBookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No bookings found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredBookings.map((booking: MeetAndGreet) => (
                    <TableRow
                      key={booking.id}
                      sx={{
                        opacity: ['CANCELLED', 'REJECTED'].includes(booking.status) ? 0.6 : 1,
                      }}
                    >
                      <TableCell>
                        <Typography fontWeight={500}>
                          {booking.studentFirstName} {booking.studentLastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {booking.studentAge} years old
                        </Typography>
                        {booking.enquiry && (
                          <Box sx={{ mt: 0.5, minWidth: 0 }}>
                            <LinkedEnquiryChip enquiry={booking.enquiry} />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={500}>{booking.contact1Name}</Typography>
                        <Typography
                          component="a"
                          href={`tel:${booking.contact1Phone}`}
                          variant="body2"
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {booking.contact1Phone}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {booking.instrument ? (
                          <Chip
                            label={booking.instrument.name}
                            size="small"
                            sx={booking.instrument.color ? {
                              backgroundColor: booking.instrument.color,
                              color: 'white',
                            } : undefined}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            label={booking.assignedTeacher
                              ? `${booking.assignedTeacher.user?.firstName || 'Unknown'} ${booking.assignedTeacher.user?.lastName || ''}`
                              : 'Unassigned'}
                            size="small"
                            sx={booking.assignedTeacher
                              ? { bgcolor: getTeacherColor(booking.assignedTeacherId) || '#4580E4', color: 'white' }
                              : { bgcolor: '#9DA5AF', color: 'white' }}
                          />
                          {canWrite && (
                            <IconButton
                              size="small"
                              aria-label="Assign teacher"
                              onClick={(e) => handleTeacherMenuOpen(e, booking)}
                              sx={{ p: 0.25 }}
                            >
                              <ArrowDropDownIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            label={statusConfig[booking.status].label}
                            size="small"
                            sx={{
                              bgcolor: statusConfig[booking.status].bgcolor,
                              color: statusConfig[booking.status].color,
                            }}
                          />
                          <ExistingFamilyChip booking={booking} />
                          {booking.notesMissing && <NotesMissingChip />}
                          <SpamCheckChip
                            spamCheck={booking.spamCheck}
                            tooltip={MEET_AND_GREET_SPAM_CHECK_TOOLTIP}
                            actionable={spamCheckActionable(booking)}
                          />
                          {getStatusActions(booking).length > 0 && (
                            <IconButton
                              size="small"
                              aria-label="Change status"
                              onClick={(e) => handleStatusMenuOpen(e, booking)}
                              sx={{ p: 0.25 }}
                            >
                              <ArrowDropDownIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                        <WaitlistClockInfo clock={booking.waitlistClock} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {booking.scheduledDateTime ? (
                            <Box
                              sx={{
                                backgroundColor: '#96DAC9',
                                color: '#080808',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                display: 'inline-block',
                                fontSize: '14px',
                              }}
                            >
                              {format(new Date(booking.scheduledDateTime), 'dd/MM/yyyy HH:mm')}
                            </Box>
                          ) : booking.preferredDateTime ? (
                            <Box
                              sx={{
                                backgroundColor: '#FFAE9E',
                                color: '#080808',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                display: 'inline-block',
                                fontSize: '14px',
                              }}
                            >
                              {format(new Date(booking.preferredDateTime), 'dd/MM/yyyy HH:mm')}
                            </Box>
                          ) : (
                            '—'
                          )}
                          {canWrite && (
                            <Tooltip title="Edit Scheduled Date & Time">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedBooking(booking);
                                  handleEditScheduled();
                                }}
                                sx={{ p: 0.25 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewDetails(booking)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {canWrite && booking.status === 'PENDING_VERIFICATION' && (
                          <>
                            {/* Resend sits FIRST, directly after View — it is the
                                common action on an unverified booking, whereas
                                "Mark as Verified" is the manual override.
                                Yellow #FFCE00 — the two resends share one glyph,
                                so COLOUR is what separates them. Yellow must
                                carry the dark #080808 glyph (13.43:1); a white
                                glyph on yellow is ~1.5:1 and unreadable. */}
                            <Tooltip
                              title={
                                verificationEmailSent(booking)
                                  ? 'Resend verification email to the primary contact'
                                  : 'Send the verification email to the primary contact'
                              }
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  bgcolor: '#FFCE00',
                                  color: '#080808',
                                  ml: 0.5,
                                  '&:hover': { bgcolor: '#e6b900' },
                                }}
                                onClick={() => handleResendVerificationClick(booking)}
                                aria-label={
                                  verificationEmailSent(booking)
                                    ? 'Resend verification email'
                                    : 'Send verification email'
                                }
                              >
                                <ForwardToInboxIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Mark as Verified">
                              <IconButton
                                size="small"
                                sx={{ color: '#FFCE00', ml: 0.5 }}
                                onClick={() => handleVerifyClick(booking)}
                              >
                                <VerifiedIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {canWrite && booking.status === 'PENDING_APPROVAL' && (
                          <>
                            {renderApproveIcon(booking)}
                            <Tooltip title="Add to Waitlist">
                              <IconButton
                                size="small"
                                sx={{ color: '#FFAE9E' }}
                                onClick={() => handleWaitlistClick(booking)}
                              >
                                <HourglassEmptyIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRejectClick(booking)}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {canWrite && booking.status === 'WAITLISTED' && renderApproveIcon(booking)}
                        {canWrite && booking.status === 'APPROVED' && (
                          <>
                            {/* Gated on the server's `canResendRegistration`
                                (#249), never on status or the token alone:
                                a legacy admin-created booking is APPROVED with no
                                link and converts manually, bypassing Stripe,
                                while a link cancelled by an email correction
                                stays resendable.
                                Yellow matches the "Complete Registration" CTA in
                                the email; it must be a filled swatch with a dark
                                glyph — a yellow glyph on white is ~1.4:1 and
                                fails WCAG AA. */}
                            {/* Shown DISABLED rather than hidden when there is no
                                link: an absent button reads as "broken", and an
                                admin can't tell that apart from "this booking
                                never had a link". The tooltip explains and points
                                at the right action. */}
                            <Tooltip title={hasExistingFamily(booking) ? EXISTING_FAMILY_TIP : resendRegistrationLabel(booking)}>
                              {/* span: MUI needs a non-disabled wrapper to fire
                                  the tooltip on a disabled control. */}
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!booking.canResendRegistration || hasExistingFamily(booking)}
                                  sx={{
                                    // Blue #4580E4 with a WHITE glyph (3.85:1,
                                    // above the 3:1 non-text floor).
                                    bgcolor: '#4580E4',
                                    color: '#FFFFFF',
                                    ml: 0.5,
                                    '&:hover': { bgcolor: '#3567c4' },
                                    '&.Mui-disabled': {
                                      bgcolor: 'action.disabledBackground',
                                      color: 'action.disabled',
                                    },
                                  }}
                                  onClick={() => handleResendRegistrationClick(booking)}
                                  aria-label={
                                    hasExistingFamily(booking)
                                      ? `Resend registration link (unavailable: ${EXISTING_FAMILY_TIP})`
                                      : booking.canResendRegistration
                                      ? 'Resend registration link'
                                      : 'Resend registration link (unavailable — no link was sent)'
                                  }
                                >
                                  <ForwardToInboxIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                        {/* #425 /qa H1: Convert BEFORE approval too (the backend accepts
                            PENDING_APPROVAL / WAITLISTED). It is how a fee taken in person
                            is recorded without Approve emailing a payment link. */}
                        {canWrite && CONVERTIBLE_STATUSES.includes(booking.status) && (
                          <Tooltip title={booking.existingFamily?.hardCollision ? EXISTING_FAMILY_TIP : 'Convert to Student'}>
                            <span>
                              <IconButton
                                size="small"
                                sx={{ color: '#96DAC9', ml: 0.5 }}
                                disabled={Boolean(booking.existingFamily?.hardCollision)}
                                onClick={() => handleConvertClick(booking)}
                                aria-label="Convert to Student"
                              >
                                <PersonAddIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {canCancel && !['CONVERTED', 'CANCELLED', 'REJECTED'].includes(booking.status) && (
                          <Tooltip title="Cancel">
                            <IconButton size="small" onClick={() => handleCancelClick(booking)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Meet & Greet Details
          {selectedBooking && (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 2 }}>
              <Chip
                label={statusConfig[selectedBooking.status].label}
                size="small"
                sx={{
                  bgcolor: statusConfig[selectedBooking.status].bgcolor,
                  color: statusConfig[selectedBooking.status].color,
                }}
              />
              <Box sx={{ ml: 1 }}>
                <ExistingFamilyChip booking={selectedBooking} />
              </Box>
              <SpamCheckChip
                spamCheck={selectedBooking.spamCheck}
                tooltip={MEET_AND_GREET_SPAM_CHECK_TOOLTIP}
                actionable={spamCheckActionable(selectedBooking)}
                sx={{ ml: 1 }}
              />
              {getStatusActions(selectedBooking).length > 0 && (
                <IconButton
                  size="small"
                  aria-label="Change status"
                  onClick={(e) => handleStatusMenuOpen(e, selectedBooking)}
                  sx={{ p: 0.25, ml: 0.5 }}
                >
                  <ArrowDropDownIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Student Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Student Information
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography>
                    <strong>Name:</strong> {selectedBooking.studentFirstName}{' '}
                    {selectedBooking.studentLastName}
                  </Typography>
                  <Typography>
                    <strong>Age:</strong> {selectedBooking.studentAge} years old
                  </Typography>
                  <Typography>
                    <strong>Instrument Interest:</strong>{' '}
                    {selectedBooking.instrument?.name || 'Not specified'}
                  </Typography>
                  {/* L2: the chip reads the LIVE list row (it refetches after notes move the stage);
                      selectedBooking is only the click-time snapshot. */}
                  {detailEnquiry && (
                    <Box sx={{ mt: 1, minWidth: 0 }}>
                      <LinkedEnquiryChip enquiry={detailEnquiry} />
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Primary Contact — pencil opens the details editor (wrong phone / email) */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Primary Contact
                  </Typography>
                  {canWrite && (
                    <Tooltip title="Edit contact details">
                      <IconButton
                        size="small"
                        aria-label="Edit contact details"
                        onClick={() => setEditDetailsOpen(true)}
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography>
                    <strong>Name:</strong> {selectedBooking.contact1Name}
                  </Typography>
                  <Typography>
                    <strong>Relationship:</strong> {selectedBooking.contact1Relationship}
                  </Typography>
                  <Typography>
                    <strong>Email:</strong> {selectedBooking.contact1Email}
                  </Typography>
                  <Typography>
                    <strong>Phone:</strong> {selectedBooking.contact1Phone}
                  </Typography>
                </Paper>
              </Grid>

              {/* Secondary Contact */}
              {selectedBooking.contact2Name && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Secondary Contact
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography>
                      <strong>Name:</strong> {selectedBooking.contact2Name}
                    </Typography>
                    <Typography>
                      <strong>Relationship:</strong> {selectedBooking.contact2Relationship}
                    </Typography>
                    <Typography>
                      <strong>Email:</strong> {selectedBooking.contact2Email}
                    </Typography>
                    <Typography>
                      <strong>Phone:</strong> {selectedBooking.contact2Phone}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* Emergency Contact */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Emergency Contact
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography>
                    <strong>Name:</strong> {selectedBooking.emergencyName}
                  </Typography>
                  <Typography>
                    <strong>Relationship:</strong> {selectedBooking.emergencyRelationship}
                  </Typography>
                  <Typography>
                    <strong>Phone:</strong> {selectedBooking.emergencyPhone}
                  </Typography>
                </Paper>
              </Grid>

              {/* Booking Info */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Booking Information
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography>
                    <strong>Requested Date:</strong>{' '}
                    {formatDate(selectedBooking.preferredDateTime)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography>
                      <strong>Scheduled Date/Time:</strong>{' '}
                      {selectedBooking.scheduledDateTime
                        ? formatDate(selectedBooking.scheduledDateTime)
                        : '—'}
                    </Typography>
                    {canWrite && (
                      <Tooltip title="Edit Scheduled Date & Time">
                        <IconButton
                          size="small"
                          onClick={handleEditScheduled}
                          sx={{ p: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography>
                    <strong>Created:</strong> {formatDate(selectedBooking.createdAt)}
                  </Typography>
                  {selectedBooking.verifiedAt && (
                    <Typography>
                      <strong>Verified:</strong> {formatDate(selectedBooking.verifiedAt)}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Teacher Assignment */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Teacher Assignment
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                      label={selectedBooking.assignedTeacher
                        ? `${selectedBooking.assignedTeacher.user?.firstName || 'Unknown'} ${selectedBooking.assignedTeacher.user?.lastName || ''}`
                        : 'Unassigned'}
                      size="small"
                      sx={selectedBooking.assignedTeacher
                        ? { bgcolor: getTeacherColor(selectedBooking.assignedTeacherId) || '#4580E4', color: 'white' }
                        : { bgcolor: '#9DA5AF', color: 'white' }}
                    />
                    {canWrite && (
                      <IconButton
                        size="small"
                        aria-label="Assign teacher"
                        onClick={(e) => handleTeacherMenuOpen(e, selectedBooking)}
                        sx={{ p: 0.25 }}
                      >
                        <ArrowDropDownIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Additional Notes */}
              {selectedBooking.additionalNotes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Additional Notes
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography>{selectedBooking.additionalNotes}</Typography>
                  </Paper>
                </Grid>
              )}

              {/* Teacher notes (#255): renders nothing without meet_and_greet.read */}
              {selectedBooking.status !== 'PENDING_VERIFICATION' && (
                <Grid item xs={12}>
                  <MeetAndGreetNotesSection
                    meetAndGreetId={selectedBooking.id}
                    childName={`${selectedBooking.studentFirstName} ${selectedBooking.studentLastName}`.trim()}
                  />
                </Grid>
              )}

              {/* Waitlist clock (#374 release B): the dates for everyone, the actions for the owner */}
              {selectedBooking.status === 'WAITLISTED' && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                    <WaitlistClockInfo clock={selectedBooking.waitlistClock} />
                    {isOwner && (
                      <WaitlistReviewActions booking={selectedBooking} onDone={() => setDetailDialogOpen(false)} />
                    )}
                  </Box>
                </Grid>
              )}

              {/* Waitlist Reason */}
              {selectedBooking.status === 'WAITLISTED' && selectedBooking.followUpNotes && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    <strong>Waitlist Reason:</strong> {selectedBooking.followUpNotes}
                  </Alert>
                </Grid>
              )}

              {/* Rejection Reason */}
              {selectedBooking.rejectionReason && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>Rejection Reason:</strong> {selectedBooking.rejectionReason}
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          {/* Resend is reachable from here too: an admin who opens a booking to
              check the parent's email address is exactly the person who then
              wants to resend. Gated on the server's `canResendRegistration`
              (#249), never status or the token alone (D7). */}
          {canWrite && selectedBooking?.canResendRegistration && (
            <Tooltip title={hasExistingFamily(selectedBooking) ? EXISTING_FAMILY_TIP : ''}>
            <span>
            <Button
              startIcon={<ForwardToInboxIcon />}
              disabled={hasExistingFamily(selectedBooking)}
              aria-label={
                hasExistingFamily(selectedBooking)
                  ? `Resend registration link (unavailable: ${EXISTING_FAMILY_TIP})`
                  : undefined
              }
              onClick={() => {
                const booking = selectedBooking;
                setDetailDialogOpen(false);
                handleResendRegistrationClick(booking);
              }}
              sx={{
                bgcolor: '#4580E4',
                color: '#FFFFFF',
                '&:hover': { bgcolor: '#3567c4' },
              }}
            >
              Resend registration link
            </Button>
            </span>
            </Tooltip>
          )}
          {canWrite && selectedBooking?.status === 'PENDING_VERIFICATION' && (
            <Button
              startIcon={<ForwardToInboxIcon />}
              sx={{
                bgcolor: '#FFCE00',
                color: '#080808',
                '&:hover': { bgcolor: '#e6b900' },
              }}
              onClick={() => {
                const booking = selectedBooking;
                setDetailDialogOpen(false);
                handleResendVerificationClick(booking);
              }}
            >
              {verificationEmailSent(selectedBooking) ? 'Resend' : 'Send'} verification email
            </Button>
          )}
          {canWrite && selectedBooking?.status === 'PENDING_APPROVAL' && (
            <>
              <Button
                color="error"
                onClick={() => {
                  setDetailDialogOpen(false);
                  handleRejectClick(selectedBooking);
                }}
              >
                Reject
              </Button>
              <Button
                sx={{ color: '#FFAE9E', borderColor: '#FFAE9E', '&:hover': { borderColor: '#ff9d8a', bgcolor: 'rgba(255,174,158,0.08)' } }}
                variant="outlined"
                onClick={() => {
                  setDetailDialogOpen(false);
                  handleWaitlistClick(selectedBooking);
                }}
              >
                Waitlist
              </Button>
              <Tooltip title={hasExistingFamily(selectedBooking) ? EXISTING_FAMILY_TIP : ''}>
                <span>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={hasExistingFamily(selectedBooking)}
                    aria-label={
                      hasExistingFamily(selectedBooking)
                        ? `Approve (unavailable: ${EXISTING_FAMILY_TIP})`
                        : undefined
                    }
                    onClick={() => {
                      setDetailDialogOpen(false);
                      handleApproveClick(selectedBooking);
                    }}
                  >
                    Approve
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          {canWrite && selectedBooking?.status === 'WAITLISTED' && (
            <Tooltip title={hasExistingFamily(selectedBooking) ? EXISTING_FAMILY_TIP : ''}>
              <span>
                <Button
                  variant="contained"
                  color="success"
                  disabled={hasExistingFamily(selectedBooking)}
                  aria-label={
                    hasExistingFamily(selectedBooking)
                      ? `Approve (unavailable: ${EXISTING_FAMILY_TIP})`
                      : undefined
                  }
                  onClick={() => {
                    setDetailDialogOpen(false);
                    handleApproveClick(selectedBooking);
                  }}
                >
                  Approve
                </Button>
              </span>
            </Tooltip>
          )}
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Meet & Greet</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Please provide a reason for rejecting this booking. This will be sent to the parent.
          </Typography>
          <TextField
            label="Rejection Reason"
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g., No available slots, age requirements not met, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectConfirm}
            disabled={!rejectReason.trim() || rejectMutation.isPending}
          >
            {rejectMutation.isPending ? 'Rejecting...' : 'Reject Booking'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Waitlist Dialog */}
      <Dialog open={waitlistDialogOpen} onClose={() => { setWaitlistDialogOpen(false); setWaitlistReason(''); setWaitlistNotifyParent(true); }} maxWidth="sm" fullWidth>
        <DialogTitle>Add to Waitlist</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This will place the booking on the waitlist. If &ldquo;Notify parent&rdquo; is enabled, an email will be sent to the parent.
            You can optionally provide a reason.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={waitlistNotifyParent}
                onChange={(e) => setWaitlistNotifyParent(e.target.checked)}
              />
            }
            label="Notify parent of being placed on waitlist"
            sx={{ mb: 1 }}
          />
          <TextField
            label="Reason (optional)"
            fullWidth
            multiline
            rows={3}
            value={waitlistReason}
            onChange={(e) => setWaitlistReason(e.target.value)}
            placeholder="e.g., All current class spots are full for this term"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setWaitlistDialogOpen(false); setWaitlistReason(''); setWaitlistNotifyParent(true); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleWaitlistConfirm}
            disabled={waitlistMutation.isPending}
            sx={{ bgcolor: '#FFAE9E', color: '#080808', '&:hover': { bgcolor: '#ff9d8a' } }}
          >
            {waitlistMutation.isPending ? 'Processing...' : 'Add to Waitlist'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog (verify / approve / cancel / resend x2) */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmAction === 'verify'
            ? 'Mark as Verified?'
            : confirmAction === 'approve'
            ? 'Approve Booking?'
            : confirmAction === 'resend-registration'
            ? 'Resend registration link?'
            : confirmAction === 'resend-verification'
            ? verificationEmailSent(selectedBooking)
              ? 'Resend verification email?'
              : 'Send verification email?'
            : 'Cancel Booking?'}
        </DialogTitle>
        <DialogContent>
          {confirmAction === 'verify' ? (
            <Typography>
              This will manually mark the email as verified and move the booking to Pending Approval status.
            </Typography>
          ) : confirmAction === 'approve' ? (
            <Typography>
              This will send a registration email to the parent with a link to complete payment
              and create their account.
            </Typography>
          ) : confirmAction === 'resend-registration' ? (
            <Box>
              <Typography sx={{ mb: 1.5 }}>
                This sends the <strong>&ldquo;Complete Your Registration&rdquo;</strong> email —
                containing a payment link that creates the family&rsquo;s account — to:
              </Typography>
              <Box component="ul" sx={{ mt: 0, mb: 1.5, pl: 3 }}>
                <li>
                  <Typography variant="body2">
                    {selectedBooking?.contact1Name} ({selectedBooking?.contact1Email})
                  </Typography>
                </li>
                {selectedBooking?.contact2Email && (
                  <li>
                    <Typography variant="body2">
                      {selectedBooking.contact2Name} ({selectedBooking.contact2Email})
                    </Typography>
                  </li>
                )}
              </Box>
              <Typography sx={{ mb: 1.5 }}>
                A <strong>new</strong> link is generated and the previous one stops working.
                The new link expires in <strong>7 days</strong>.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last sent:{' '}
                <strong>
                  {selectedBooking ? lastSentLabel(selectedBooking, 'registration') : '—'}
                </strong>
              </Typography>
            </Box>
          ) : confirmAction === 'resend-verification' ? (
            <Box>
              <Typography sx={{ mb: 1.5 }}>
                This sends the <strong>&ldquo;Verify Your Meet &amp; Greet Booking&rdquo;</strong>{' '}
                email to {selectedBooking?.contact1Name} ({selectedBooking?.contact1Email}).
              </Typography>
              {verificationEmailSent(selectedBooking) ? (
                <>
                  <Typography sx={{ mb: 1.5 }}>
                    A new verification link is generated and the previous one stops working.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last sent:{' '}
                    <strong>
                      {selectedBooking ? lastSentLabel(selectedBooking, 'verification') : '—'}
                    </strong>
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Last sent: <strong>Not sent yet</strong>
                </Typography>
              )}
            </Box>
          ) : (
            <Typography>
              This will cancel the booking. The parent will not be notified automatically.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={
              confirmAction === 'approve'
                ? 'success'
                : confirmAction === 'verify'
                ? 'warning'
                : confirmAction === 'resend-registration'
                ? 'primary'
                : confirmAction === 'resend-verification'
                ? 'inherit'
                : 'error'
            }
            onClick={handleConfirmAction}
            disabled={
              approveMutation.isPending ||
              cancelMutation.isPending ||
              updateMutation.isPending ||
              markVerifiedMutation.isPending ||
              resendRegistrationMutation.isPending ||
              resendVerificationMutation.isPending
            }
            // Yellow carries a dark label — white on #FFCE00 fails contrast.
            sx={
              confirmAction === 'verify' || confirmAction === 'resend-verification'
                ? { bgcolor: '#FFCE00', color: '#080808', '&:hover': { bgcolor: '#e6b900' } }
                : undefined
            }
          >
            {approveMutation.isPending ||
            cancelMutation.isPending ||
            updateMutation.isPending ||
            markVerifiedMutation.isPending ||
            resendRegistrationMutation.isPending ||
            resendVerificationMutation.isPending
              ? 'Processing...'
              : confirmAction === 'verify'
              ? 'Mark as Verified'
              : confirmAction === 'approve'
              ? 'Approve & Send Email'
              : confirmAction === 'resend-registration'
              ? 'Resend registration link'
              : confirmAction === 'resend-verification'
              ? verificationEmailSent(selectedBooking)
                ? 'Resend verification email'
                : 'Send verification email'
              : 'Cancel Booking'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual-delivery dialog — shown ONLY when a resend regenerated the link
          but every email bounced. The old link is already dead by then, so this
          is the admin's recovery path; a snackbar would vanish before they could
          copy the URL. */}
      <Dialog
        open={manualLink !== null}
        onClose={() => setManualLink(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Link created, but the email didn&rsquo;t send</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {manualLink?.message}
          </Alert>
          {manualLink && manualLink.failures.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Delivery failed to: {manualLink.failures.join(', ')}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mb: 1 }}>
            Send this link to the parent yourself. It expires in 7 days.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={manualLink?.url ?? ''}
            InputProps={{ readOnly: true }}
            onFocus={(e) => e.currentTarget.select()}
            sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 13 } }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Don&rsquo;t resend again unless you have to — each resend creates a new link
            and stops this one working.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              if (!manualLink) return;
              try {
                await navigator.clipboard.writeText(manualLink.url);
                setManualLinkCopied(true);
              } catch {
                // Clipboard can be blocked (permissions, insecure context).
                // The field is selectable, so manual copy still works.
                setManualLinkCopied(false);
              }
            }}
          >
            {manualLinkCopied ? 'Copied' : 'Copy link'}
          </Button>
          <Button variant="contained" onClick={() => setManualLink(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert to Student Dialog */}
      <ConvertToStudentDialog
        open={convertDialogOpen}
        booking={selectedBooking}
        onClose={() => setConvertDialogOpen(false)}
        onConverted={() => {
          setConvertDialogOpen(false);
          setSelectedBooking(null);
        }}
      />

      {/* Status Actions Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleStatusMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {statusMenuBooking && getStatusActions(statusMenuBooking).map((item) => {
          const disabledReason = 'disabledReason' in item ? item.disabledReason : undefined;
          // #430 review L4: no Tooltip/span wrapper inside the menu list (it made `ul > span > li`
          // and took MenuList's keyboard props). A blocked item is a disabled MenuItem whose
          // reason is its secondary text.
          return (
            <MenuItem
              key={item.action}
              onClick={() => handleStatusAction(item.action)}
              disabled={Boolean(disabledReason)}
              aria-label={disabledReason ? `${item.label} (unavailable: ${disabledReason})` : undefined}
              sx={{ minWidth: 180, ...(disabledReason ? { maxWidth: 320, whiteSpace: 'normal' } : {}) }}
            >
              <ListItemIcon sx={{ color: item.color }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} secondary={disabledReason} />
            </MenuItem>
          );
        })}
      </Menu>

      {/* Teacher Assignment Menu */}
      <Menu
        anchorEl={teacherMenuAnchor}
        open={Boolean(teacherMenuAnchor)}
        onClose={handleTeacherMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem
          onClick={() => handleTeacherAssign(null)}
          sx={{ minWidth: 180 }}
          selected={!teacherMenuBooking?.assignedTeacherId}
        >
          <ListItemIcon>
            <PersonAddIcon fontSize="small" sx={{ color: '#9DA5AF' }} />
          </ListItemIcon>
          <ListItemText><em>Unassigned</em></ListItemText>
        </MenuItem>
        {(teachers as Teacher[] | undefined)?.filter((t) => t.isActive && t.User).map((teacher) => (
          <MenuItem
            key={teacher.id}
            onClick={() => handleTeacherAssign(teacher.id)}
            sx={{ minWidth: 180 }}
            selected={teacherMenuBooking?.assignedTeacherId === teacher.id}
          >
            <ListItemIcon>
              <PersonAddIcon fontSize="small" sx={{ color: teacher.color || '#4580E4' }} />
            </ListItemIcon>
            <ListItemText>{teacher.User?.firstName || 'Unknown'} {teacher.User?.lastName || ''}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Schedule Dialog */}
      <EditMeetAndGreetDetailsDialog
        open={editDetailsOpen}
        booking={selectedBooking}
        onClose={() => setEditDetailsOpen(false)}
        onSaved={(updated) => setSelectedBooking((prev) => (prev ? { ...prev, ...updated } : prev))}
      />

      <CreateMeetAndGreetDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      <AdminScheduleDialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        onSave={handleSaveScheduled}
        currentDateTime={selectedBooking?.scheduledDateTime || null}
        bookingId={selectedBooking?.id || ''}
        isSaving={updateMutation.isPending}
      />
    </Box>
  );
}
